import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import type { InfluenceLevel, InterestedParty, InterestedPartyPayload, PartyType } from './types';
import {
  computePartyIndex,
  computePartySeverity,
  SCORE_OPTIONS,
} from './scoringUtils';

const PARTY_TYPES: PartyType[] = ['interna', 'externa'];
const INFLUENCE_LEVELS: InfluenceLevel[] = ['alta', 'media', 'baja'];

const PARTY_TYPE_LABEL: Record<PartyType, string> = {
  interna: 'Interna',
  externa: 'Externa',
};

const INFLUENCE_LABEL: Record<InfluenceLevel, string> = {
  alta: 'Alta',
  media: 'Media',
  baja: 'Baja',
};

interface InterestedPartyFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: InterestedParty | null;
  onSubmit: (payload: InterestedPartyPayload) => Promise<void>;
}

export function InterestedPartyFormModal({
  open,
  onOpenChange,
  initial,
  onSubmit,
}: InterestedPartyFormModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<PartyType>('externa');
  const [needs, setNeeds] = useState('');
  const [expectations, setExpectations] = useState('');
  const [influence, setInfluence] = useState<InfluenceLevel>('media');
  const [monitoringMethod, setMonitoringMethod] = useState('');
  const [interestScore, setInterestScore] = useState<number | ''>('');
  const [powerScore, setPowerScore] = useState<number | ''>('');
  const [occurrence, setOccurrence] = useState<number | ''>('');
  const [detection, setDetection] = useState<number | ''>('');
  const [strategy, setStrategy] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const liveScoring = useMemo(() => {
    if (interestScore === '' || powerScore === '' || occurrence === '' || detection === '') {
      return null;
    }
    const severity = computePartySeverity(interestScore, powerScore);
    const index = computePartyIndex(severity, occurrence, detection);
    return { severity, index };
  }, [interestScore, powerScore, occurrence, detection]);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setType(initial?.type ?? 'externa');
    setNeeds(initial?.needs ?? '');
    setExpectations(initial?.expectations ?? '');
    setInfluence(initial?.influence ?? 'media');
    setMonitoringMethod(initial?.monitoringMethod ?? '');
    setInterestScore(initial?.interestScore ?? '');
    setPowerScore(initial?.powerScore ?? '');
    setOccurrence(initial?.occurrence ?? '');
    setDetection(initial?.detection ?? '');
    setStrategy(initial?.strategy ?? '');
  }, [open, initial]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        type,
        needs: needs.trim(),
        expectations: expectations.trim(),
        influence,
        monitoringMethod: monitoringMethod.trim() || null,
        interestScore: interestScore === '' ? null : interestScore,
        powerScore: powerScore === '' ? null : powerScore,
        occurrence: occurrence === '' ? null : occurrence,
        detection: detection === '' ? null : detection,
        strategy: strategy.trim() || null,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {initial ? 'Editar parte interesada' : 'Nueva parte interesada'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <div>
            <label htmlFor='party-name' className='block text-sm font-medium mb-1'>
              Nombre
            </label>
            <input
              id='party-name'
              type='text'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={255}
              placeholder='Ej: Clientes clave'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <label htmlFor='party-type' className='block text-sm font-medium mb-1'>
                Tipo
              </label>
              <select
                id='party-type'
                value={type}
                onChange={(e) => setType(e.target.value as PartyType)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                {PARTY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {PARTY_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor='party-influence' className='block text-sm font-medium mb-1'>
                Influencia
              </label>
              <select
                id='party-influence'
                value={influence}
                onChange={(e) => setInfluence(e.target.value as InfluenceLevel)}
                className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
              >
                {INFLUENCE_LEVELS.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {INFLUENCE_LABEL[lvl]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label htmlFor='party-needs' className='block text-sm font-medium mb-1'>
              Necesidades
            </label>
            <textarea
              id='party-needs'
              value={needs}
              onChange={(e) => setNeeds(e.target.value)}
              required
              rows={3}
              placeholder='Qué necesita esta parte interesada…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div>
            <label htmlFor='party-expectations' className='block text-sm font-medium mb-1'>
              Expectativas
            </label>
            <textarea
              id='party-expectations'
              value={expectations}
              onChange={(e) => setExpectations(e.target.value)}
              required
              rows={3}
              placeholder='Qué espera de la organización…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <fieldset className='rounded-md border border-border p-3 space-y-3'>
            <legend className='text-sm font-medium px-1'>Matriz poder / interés y S × O × D</legend>
            <div className='grid grid-cols-2 gap-3'>
              <div>
                <label htmlFor='party-interest' className='block text-xs font-medium mb-1'>
                  Interés (I)
                </label>
                <select
                  id='party-interest'
                  value={interestScore}
                  onChange={(e) =>
                    setInterestScore(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
                >
                  <option value=''>—</option>
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='party-power' className='block text-xs font-medium mb-1'>
                  Poder (P)
                </label>
                <select
                  id='party-power'
                  value={powerScore}
                  onChange={(e) =>
                    setPowerScore(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
                >
                  <option value=''>—</option>
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='party-occurrence' className='block text-xs font-medium mb-1'>
                  Ocurrencia (O)
                </label>
                <select
                  id='party-occurrence'
                  value={occurrence}
                  onChange={(e) =>
                    setOccurrence(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
                >
                  <option value=''>—</option>
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor='party-detection' className='block text-xs font-medium mb-1'>
                  Detección (D)
                </label>
                <select
                  id='party-detection'
                  value={detection}
                  onChange={(e) =>
                    setDetection(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  className='w-full rounded-md border border-input bg-background px-2 py-2 text-sm'
                >
                  <option value=''>—</option>
                  {SCORE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {liveScoring && (
              <p className='text-sm text-muted-foreground'>
                Severidad (S): <strong>{liveScoring.severity}</strong> · Índice:{' '}
                <strong>{liveScoring.index}</strong>
              </p>
            )}
          </fieldset>
          <div>
            <label htmlFor='party-strategy' className='block text-sm font-medium mb-1'>
              Estrategia (opcional)
            </label>
            <textarea
              id='party-strategy'
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              rows={2}
              placeholder='Estrategia de gestión de la relación…'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div>
            <label htmlFor='party-monitoring' className='block text-sm font-medium mb-1'>
              Método de seguimiento (opcional)
            </label>
            <input
              id='party-monitoring'
              type='text'
              value={monitoringMethod}
              onChange={(e) => setMonitoringMethod(e.target.value)}
              maxLength={500}
              placeholder='Ej: Encuesta anual, reunión trimestral'
              className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
            />
          </div>
          <div className='flex justify-end gap-2'>
            <button
              type='button'
              onClick={() => onOpenChange(false)}
              className='px-4 py-2 text-sm rounded-md border border-border hover:bg-muted'
            >
              Cancelar
            </button>
            <button
              type='submit'
              disabled={submitting || !name.trim() || !needs.trim() || !expectations.trim()}
              className='px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground disabled:opacity-50'
            >
              {submitting ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
